import i18next from '@/lib/i18next';

const GO_PUBLISH_EXCEPTION_TYPE = 'GoPublishValidationError';

// The server's warning when a language has no content at all.
const GO_LANGUAGE_MISSING_MESSAGES = {
  eng: 'English content is missing',
  spa: 'Spanish content is missing',
};

/**
 * Classifies missing-content warnings per language: 'all' when the whole language is
 * missing, 'some' for any other warning in it.
 * @returns {Object<string, 'all'|'some'>}
 */
export function goMissingContentByLang(warnings = []) {
  return warnings.reduce((byLang, warning) => {
    const lang = warning?.lang;
    if (!lang) return byLang;
    const missing = warning.message === GO_LANGUAGE_MISSING_MESSAGES[lang] ? 'all' : 'some';
    return { ...byLang, [lang]: byLang[lang] === 'all' ? 'all' : missing };
  }, {});
}

/**
 * Maps a failed publish to GraphicOrganizerErrorsModal rows.
 * @returns {Array<{pageId: string|null, moduleId: null, reason: string}>} [] for any other error
 */
export function goPublishErrorRows(error) {
  const meta = error?.response?.data?.meta;
  if (meta?.exception_type !== GO_PUBLISH_EXCEPTION_TYPE) return [];

  return (meta.extra_information?.assessment_item_errors || []).map((item) => ({
    pageId: item.page_id ?? null,
    moduleId: null,
    reason: item.message,
  }));
}

/**
 * A failed request with no HTTP status (network) or a 5xx, i.e. worth retrying.
 * Accepts a thrown fetch error (`error.response`) or a thrown response itself.
 */
export function isTransientFetchError(error) {
  const status = error?.response?.status ?? error?.status;
  return typeof status !== 'number' || status >= 500;
}

/**
 * A failed GO render request as display text for TEIDataError and GraphicOrganizerErrorsModal.
 * @returns {string}
 */
export function goFetchErrorMessage(error) {
  const message = (error?.response?.data ?? error?.data)?.meta?.message ?? error?.message;
  return typeof message === 'string' && message
    ? message
    : i18next.t('This slide could not be published.');
}

/**
 * Concatenates assessment_item_errors/warnings lists, dropping repeats of the same
 * (lang, row, col, message).
 * @returns {Array<Object>}
 */
export function mergeGoIssues(...lists) {
  const seen = new Set();
  return lists.flat().filter((item) => {
    if (!item) return false;
    const key = JSON.stringify([item.lang ?? null, item.row ?? null, item.col ?? null, item.message]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * The `assessmentStore.teis` entry for a rendered GO page. Validation errors set `error`
 * to their messages so the page blocks publish and shows TEIDataError.
 * @returns {Object}
 */
export function goTeiEntry(pageId, { teiData, errors = [], warnings = [] }) {
  const entry = {
    ...teiData,
    id: pageId,
    assessment_item_errors: errors,
    assessment_item_warnings: warnings,
  };
  if (errors.length) {
    entry.error = errors.map((item) => item.message).filter(Boolean).join(' ')
      || i18next.t('This slide could not be published.');
  }
  return entry;
}
